import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { sanityClient, urlFor } from '../../lib/sanity';
import { PortableText } from '@portabletext/react';

function CourseDetail() {
  const { category, slug } = useParams();
  const [course, setCourse] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [selectedImage, setSelectedImage] = React.useState(null);

  // Category display names
  const categoryNames = {
    'kids': 'Kids',
    'teens-adults': 'Teens & Adults',
    'private-group': 'Private Group',
    'corporate': 'Corporate',
  };

  React.useEffect(() => {
    setLoading(true);
    sanityClient
      .fetch(
        `*[_type == "course" && category == $category && slug.current == $slug && isActive == true][0] {
          _id,
          name,
          slug,
          category,
          mainImage,
          shortDescription,
          longDescription,
          gallery,
          duration,
          ageRange,
          language,
          pdfLink,
          highlights
        }`,
        { category, slug }
      )
      .then((data) => {
        if (!data) {
          setError('Course not found');
        } else {
          setCourse(data);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching course:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [category, slug]);

  // Redirect to 404 if course not found
  if (!loading && error === 'Course not found') {
    return <Navigate to="/academy" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-[#F4900C]"></div>
          <p className="text-gray-600 mt-4 text-lg">Loading course details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-xl mb-4">Error: {error}</p>
          <Link
            to="/academy"
            className="inline-block bg-gradient-to-r from-[#FED700] to-[#F4900C] text-gray-900 px-6 py-3 rounded-full font-semibold hover:shadow-lg transition-all duration-300"
          >
            Back to Academy
          </Link>
        </div>
      </div>
    );
  }

  if (!course) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-orange-50">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="absolute top-10 left-10 w-96 h-96 bg-[#FED700]/20 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#F4900C]/20 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        
        <div className="container-custom relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
            <Link to="/academy" className="hover:text-[#F4900C] transition-colors">
              Academy
            </Link>
            <span>→</span>
            <Link to={`/academy/${category}`} className="hover:text-[#F4900C] transition-colors">
              {categoryNames[category]}
            </Link>
            <span>→</span>
            <span className="text-gray-900 font-semibold">{course.name}</span>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left: Course Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#FED700] to-[#F4900C] rounded-2xl transform rotate-3"></div>
              <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
                {course.mainImage ? (
                  <img
                    src={urlFor(course.mainImage)
                      .width(800)
                      .height(600)
                      .format('jpg')
                      .quality(90)
                      .url()}
                    alt={course.name}
                    className="w-full h-auto"
                  />
                ) : (
                  <div className="w-full h-96 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                    <span className="text-9xl opacity-30">📚</span>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Course Info */}
            <div>
              <div className="inline-block bg-gradient-to-r from-[#FED700]/20 to-[#F4900C]/20 px-4 py-2 rounded-full mb-4">
                <span className="font-semibold text-gray-800">{categoryNames[category]}</span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {course.name}
              </h1>

              <p className="text-xl text-gray-700 mb-8">
                {course.shortDescription}
              </p>

              {/* Course Meta */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-white p-4 rounded-xl shadow">
                  <div className="flex items-center text-gray-600 mb-2">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-semibold">Duration</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{course.duration}</p>
                </div>

                <div className="bg-white p-4 rounded-xl shadow">
                  <div className="flex items-center text-gray-600 mb-2">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span className="text-sm font-semibold">Age Range</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{course.ageRange}</p>
                </div>

                <div className="bg-white p-4 rounded-xl shadow">
                  <div className="flex items-center text-gray-600 mb-2">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                    </svg>
                    <span className="text-sm font-semibold">Language</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{course.language}</p>
                </div>

                {course.pdfLink && (
                  <div className="bg-white p-4 rounded-xl shadow">
                    <a
                      href={course.pdfLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center text-[#F4900C] hover:text-[#FED700] transition-colors h-full"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="font-semibold">Download PDF</span>
                    </a>
                  </div>
                )}
              </div>

              {/* CTA Button */}
              <Link
                to="/contact"
                className="inline-block w-full text-center bg-gradient-to-r from-[#FED700] to-[#F4900C] text-gray-900 px-8 py-4 rounded-full font-bold text-lg hover:shadow-lg transition-all duration-300"
              >
                Enroll Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Course Highlights */}
      {course.highlights && course.highlights.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container-custom">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              What You'll <span className="text-[#F4900C]">Learn</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {course.highlights.map((highlight, index) => (
                <div key={index} className="flex items-start gap-4 bg-gradient-to-br from-yellow-50 to-orange-50 p-6 rounded-xl">
                  <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-[#FED700] to-[#F4900C] rounded-full flex items-center justify-center text-white font-bold">
                    ✓
                  </div>
                  <p className="text-gray-700 leading-relaxed">{highlight}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Course Description */}
      <section className="py-16">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Course <span className="text-[#F4900C]">Overview</span>
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              {course.longDescription && (
                <PortableText 
                  value={course.longDescription}
                  components={{
                    block: {
                      normal: ({children}) => <p className="mb-4 leading-relaxed">{children}</p>,
                      h1: ({children}) => <h1 className="text-3xl font-bold text-gray-900 mt-8 mb-4">{children}</h1>,
                      h2: ({children}) => <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{children}</h2>,
                      h3: ({children}) => <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">{children}</h3>,
                      h4: ({children}) => <h4 className="text-lg font-bold text-gray-900 mt-6 mb-3">{children}</h4>,
                    },
                    list: {
                      bullet: ({children}) => <ul className="list-disc list-inside mb-4 ml-4 space-y-2">{children}</ul>,
                      number: ({children}) => <ol className="list-decimal list-inside mb-4 ml-4 space-y-2">{children}</ol>,
                    },
                    listItem: {
                      bullet: ({children}) => <li className="text-gray-700 leading-relaxed">{children}</li>,
                      number: ({children}) => <li className="text-gray-700 leading-relaxed">{children}</li>,
                    },
                    marks: {
                      strong: ({children}) => <strong className="font-bold text-gray-900">{children}</strong>,
                      em: ({children}) => <em className="italic">{children}</em>,
                      link: ({children, value}) => (
                        <a 
                          href={value.href} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-[#F4900C] hover:text-[#FED700] underline"
                        >
                          {children}
                        </a>
                      ),
                    },
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {course.gallery && course.gallery.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container-custom">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              Course <span className="text-[#F4900C]">Gallery</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {course.gallery.map((image, index) => (
                <div
                  key={index}
                  onClick={() => setSelectedImage(image)}
                  className="relative aspect-square overflow-hidden rounded-xl shadow-lg cursor-zoom-in hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                >
                  <img
                    src={urlFor(image)
                      .width(400)
                      .height(400)
                      .format('jpg')
                      .quality(85)
                      .url()}
                    alt={`Gallery ${index + 1}`}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 z-10"
          >
            ×
          </button>
          <img
            src={urlFor(selectedImage)
              .width(1200)
              .fit('max')
              .url()}
            alt="Gallery"
            className="max-h-[90vh] max-w-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#FED700] to-[#F4900C]">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-gray-800 mb-8">
              Contact us to enroll in this course or learn more about our programs
            </p>
            <Link
              to="/contact"
              className="inline-block bg-white text-[#F4900C] px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors duration-300 shadow-lg"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default CourseDetail;